import { ref, watch } from "@vue/composition-api";

import { Session, Document } from "@/tools/msa/models";
import { compareNullableDates } from "@/tools/msa/utils/comparator";
import { CodeMatchItem, CustomMatchItem, Item } from "@/tools/msa/models/item";

interface Props {
  session: Session;
  document: Document;
}

interface Wrapper {
  item: Item;
  documentIndex?: number;
}

export const useMapForCodes = (props: Props) => {
  const map = ref<Map<string, Wrapper[]>>(new Map());

  const documentsIndices = ref<Map<Document, number>>(new Map());

  watch(
    () => props.session.documents,
    documents => {
      documentsIndices.value = new Map(
        documents.map((document, index) => [document, index])
      );
    },
    { immediate: true }
  );

  watch(
    [() => props.session, () => props.document, () => props.document.items],
    () => {
      map.value = new Map(
        Array.from(
          props.session.documents
            .map((document, index) => ({ index, ...document }))
            .flatMap(document => document.items)
            .filter(
              item => item.type === "code_match" || item.type === "custom_match"
            )
            .map(item =>
              item.type === "code_match"
                ? (item as CodeMatchItem)
                : (item as CustomMatchItem)
            )
            .reduce(
              (
                map: Map<string, Wrapper[]>,
                item: CodeMatchItem | CustomMatchItem
              ) =>
                map.set(item.match.coding.code, [
                  ...(map.get(item.match.coding.code) || []),
                  {
                    item,
                    documentIndex: documentsIndices.value.get(item.document)
                  }
                ]),
              new Map()
            )
            .entries()
        ).map(([code, items]) => [
          code,
          items.sort((a, b) =>
            compareNullableDates(a.item.document.date, b.item.document.date)
          )
        ])
      );
    },
    { immediate: true }
  );

  return { map };
};
